import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
    Add,
    ContentCopy,
    Download,
    Redo,
    SaveAlt,
    Search,
    Undo,
} from '@mui/icons-material'

const MAX_RENDER_ROWS = 400
const MAX_RENDER_COLS = 60
const MAX_HISTORY = 100

const normalizeName = (value, fallback = 'table.xlsx') => {
    const text = String(value || '').trim() || fallback
    return text.replace(/[\\/:*?"<>|]+/g, '_')
}

const ensureXlsxName = (value) => {
    const safe = normalizeName(value, 'table.xlsx')
    if (/\.xlsx$/i.test(safe)) return safe
    return `${safe.replace(/\.[^.]+$/, '')}.xlsx`
}

const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    link.style.display = 'none'
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.setTimeout(() => URL.revokeObjectURL(url), 1500)
}

const toCellInputValue = (cell) => {
    if (!cell) return ''
    if (cell.f) return `=${cell.f}`
    if (cell.v === undefined || cell.v === null) return ''
    if (cell.v instanceof Date) return cell.v.toISOString()
    return String(cell.v)
}

const parseInputValue = (value) => {
    const text = String(value ?? '')
    if (!text) return null
    if (text.startsWith('=')) return { t: 'n', f: text.slice(1) }
    if (/^-?(?:\d+\.?\d*|\.\d+)$/.test(text.trim())) return { t: 'n', v: Number(text) }
    if (/^(true|false)$/i.test(text.trim())) return { t: 'b', v: /^true$/i.test(text.trim()) }
    return { t: 's', v: text }
}

const cloneCell = (cell) => {
    if (!cell) return null
    const next = { ...cell }
    if (cell.s && typeof cell.s === 'object') next.s = { ...cell.s }
    return next
}

const cellsEqual = (a, b) => JSON.stringify(a || null) === JSON.stringify(b || null)

const getWorkbookMime = () => 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

export default function SpreadsheetWorkspace({
    blob = null,
    source = '',
    filename = 'table.xlsx',
    title = '',
    readOnly = false,
    onDirtyChange,
    onSaveFile,
}) {
    const workbookRef = useRef(null)
    const xlsxRef = useRef(null)
    const rootRef = useRef(null)
    const editingInputRef = useRef(null)

    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [sheetName, setSheetName] = useState('')
    const [revision, setRevision] = useState(0)
    const [selected, setSelected] = useState({ r: 0, c: 0 })
    const [editingAddress, setEditingAddress] = useState('')
    const [editingValue, setEditingValue] = useState('')
    const [search, setSearch] = useState('')
    const [history, setHistory] = useState([])
    const [historyIndex, setHistoryIndex] = useState(-1)
    const [dirty, setDirty] = useState(false)
    const [status, setStatus] = useState('')

    const markDirty = useCallback((value) => {
        setDirty(value)
        onDirtyChange?.(value)
    }, [onDirtyChange])

    useEffect(() => {
        let cancelled = false

        const load = async () => {
            setLoading(true)
            setError('')
            setStatus('')
            setHistory([])
            setHistoryIndex(-1)
            setRevision(0)
            setEditingAddress('')
            markDirty(false)

            try {
                const imported = await import('xlsx')
                const XLSX = imported?.default && imported.default.read ? imported.default : imported
                xlsxRef.current = XLSX

                let arrayBuffer
                if (blob instanceof Blob) {
                    arrayBuffer = await blob.arrayBuffer()
                } else if (source) {
                    const response = await fetch(source, { credentials: 'include' })
                    if (!response.ok) throw new Error(`Не удалось загрузить таблицу: HTTP ${response.status}`)
                    arrayBuffer = await response.arrayBuffer()
                } else {
                    throw new Error('Файл таблицы недоступен')
                }

                if (cancelled) return

                const workbook = XLSX.read(arrayBuffer, {
                    type: 'array',
                    cellDates: true,
                    cellFormula: true,
                    cellStyles: true,
                })

                if (!workbook?.SheetNames?.length) throw new Error('В файле нет листов')

                workbookRef.current = workbook
                setSheetName(workbook.SheetNames[0])
                setSelected({ r: 0, c: 0 })
                setRevision((value) => value + 1)
            } catch (loadError) {
                if (!cancelled) {
                    setError(
                        loadError?.message?.includes('Cannot find module') || loadError?.message?.includes('Failed to resolve module')
                            ? 'Для просмотра таблиц установите зависимость xlsx: npm install xlsx'
                            : (loadError?.message || 'Не удалось открыть таблицу')
                    )
                }
            } finally {
                if (!cancelled) setLoading(false)
            }
        }

        load()

        return () => {
            cancelled = true
        }
    }, [blob, markDirty, source])

    useEffect(() => {
        if (!editingAddress) return
        const timer = window.setTimeout(() => {
            editingInputRef.current?.focus?.()
            editingInputRef.current?.select?.()
        }, 0)
        return () => window.clearTimeout(timer)
    }, [editingAddress])

    const XLSX = xlsxRef.current
    const workbook = workbookRef.current
    const sheet = workbook && sheetName ? workbook.Sheets?.[sheetName] : null

    const range = useMemo(() => {
        if (!XLSX || !sheet) return { s: { r: 0, c: 0 }, e: { r: 0, c: 0 } }
        try {
            return XLSX.utils.decode_range(sheet['!ref'] || 'A1:A1')
        } catch (_error) {
            return { s: { r: 0, c: 0 }, e: { r: 0, c: 0 } }
        }
    }, [XLSX, sheet, revision])

    const rowsCount = Math.min(MAX_RENDER_ROWS, Math.max(1, range.e.r + 1))
    const colsCount = Math.min(MAX_RENDER_COLS, Math.max(1, range.e.c + 1))
    const truncatedRows = Math.max(0, range.e.r + 1 - rowsCount)
    const truncatedCols = Math.max(0, range.e.c + 1 - colsCount)

    const getAddress = useCallback((r, c) => {
        if (!XLSX) return `R${r + 1}C${c + 1}`
        return XLSX.utils.encode_cell({ r, c })
    }, [XLSX])

    const getCell = useCallback((r, c) => {
        if (!sheet || !XLSX) return null
        return sheet[getAddress(r, c)] || null
    }, [XLSX, getAddress, sheet])

    const getCellDisplay = useCallback((r, c) => {
        const cell = getCell(r, c)
        if (!cell) return ''
        if (cell.f && (cell.v === undefined || cell.v === null || cell.v === '')) return `=${cell.f}`
        try {
            const formatted = XLSX?.utils?.format_cell?.(cell)
            return formatted === undefined || formatted === null ? toCellInputValue(cell) : String(formatted)
        } catch (_error) {
            return toCellInputValue(cell)
        }
    }, [XLSX, getCell])

    const selectedAddress = getAddress(selected.r, selected.c)
    const selectedCell = getCell(selected.r, selected.c)
    const selectedFormulaValue = toCellInputValue(selectedCell)

    const pushHistory = useCallback((entry) => {
        setHistory((prev) => {
            const base = prev.slice(0, historyIndex + 1)
            const next = [...base, entry].slice(-MAX_HISTORY)
            setHistoryIndex(next.length - 1)
            return next
        })
    }, [historyIndex])

    const applyCell = useCallback((sheetKey, address, nextCell, { recordHistory = true } = {}) => {
        const currentWorkbook = workbookRef.current
        const currentXlsx = xlsxRef.current
        const targetSheet = currentWorkbook?.Sheets?.[sheetKey]
        if (!targetSheet || !currentXlsx) return false

        const before = cloneCell(targetSheet[address])
        const after = cloneCell(nextCell)
        if (cellsEqual(before, after)) return false

        if (after) targetSheet[address] = after
        else delete targetSheet[address]

        const decoded = currentXlsx.utils.decode_cell(address)
        let currentRange
        try {
            currentRange = currentXlsx.utils.decode_range(targetSheet['!ref'] || 'A1:A1')
        } catch (_error) {
            currentRange = { s: { r: 0, c: 0 }, e: { r: 0, c: 0 } }
        }
        currentRange.e.r = Math.max(currentRange.e.r, decoded.r)
        currentRange.e.c = Math.max(currentRange.e.c, decoded.c)
        targetSheet['!ref'] = currentXlsx.utils.encode_range(currentRange)

        if (recordHistory) pushHistory({ sheetName: sheetKey, changes: [{ address, before, after }] })

        markDirty(true)
        setRevision((value) => value + 1)
        return true
    }, [markDirty, pushHistory])

    const applyChanges = useCallback((sheetKey, changes, { recordHistory = true } = {}) => {
        const currentWorkbook = workbookRef.current
        const currentXlsx = xlsxRef.current
        const targetSheet = currentWorkbook?.Sheets?.[sheetKey]
        if (!targetSheet || !currentXlsx || !Array.isArray(changes) || !changes.length) return

        const effective = []
        let currentRange
        try {
            currentRange = currentXlsx.utils.decode_range(targetSheet['!ref'] || 'A1:A1')
        } catch (_error) {
            currentRange = { s: { r: 0, c: 0 }, e: { r: 0, c: 0 } }
        }

        changes.forEach(({ address, nextCell }) => {
            const before = cloneCell(targetSheet[address])
            const after = cloneCell(nextCell)
            if (cellsEqual(before, after)) return

            if (after) targetSheet[address] = after
            else delete targetSheet[address]

            const decoded = currentXlsx.utils.decode_cell(address)
            currentRange.e.r = Math.max(currentRange.e.r, decoded.r)
            currentRange.e.c = Math.max(currentRange.e.c, decoded.c)
            effective.push({ address, before, after })
        })

        if (!effective.length) return

        targetSheet['!ref'] = currentXlsx.utils.encode_range(currentRange)
        if (recordHistory) pushHistory({ sheetName: sheetKey, changes: effective })
        markDirty(true)
        setRevision((value) => value + 1)
    }, [markDirty, pushHistory])

    const startEdit = useCallback((r, c, initial = null) => {
        if (readOnly) return
        const address = getAddress(r, c)
        setSelected({ r, c })
        setEditingAddress(address)
        setEditingValue(initial === null ? toCellInputValue(getCell(r, c)) : String(initial))
    }, [getAddress, getCell, readOnly])

    const commitEdit = useCallback(() => {
        if (!editingAddress || readOnly) {
            setEditingAddress('')
            return
        }
        const nextCell = parseInputValue(editingValue)
        applyCell(sheetName, editingAddress, nextCell)
        setEditingAddress('')
    }, [applyCell, editingAddress, editingValue, readOnly, sheetName])

    const undo = useCallback(() => {
        if (historyIndex < 0) return
        const entry = history[historyIndex]
        const targetSheet = workbookRef.current?.Sheets?.[entry.sheetName]
        if (!targetSheet) return

        entry.changes.forEach(({ address, before }) => {
            if (before) targetSheet[address] = cloneCell(before)
            else delete targetSheet[address]
        })
        setHistoryIndex((value) => value - 1)
        markDirty(true)
        setRevision((value) => value + 1)
    }, [history, historyIndex, markDirty])

    const redo = useCallback(() => {
        if (historyIndex >= history.length - 1) return
        const entry = history[historyIndex + 1]
        const targetSheet = workbookRef.current?.Sheets?.[entry.sheetName]
        if (!targetSheet) return

        entry.changes.forEach(({ address, after }) => {
            if (after) targetSheet[address] = cloneCell(after)
            else delete targetSheet[address]
        })
        setHistoryIndex((value) => value + 1)
        markDirty(true)
        setRevision((value) => value + 1)
    }, [history, historyIndex, markDirty])

    const copySelected = useCallback(async () => {
        const value = getCellDisplay(selected.r, selected.c)
        try {
            await navigator.clipboard?.writeText?.(value)
            setStatus(`Скопировано: ${selectedAddress}`)
        } catch (_error) {
            setStatus('Не удалось скопировать в буфер обмена')
        }
    }, [getCellDisplay, selected.c, selected.r, selectedAddress])

    const pasteText = useCallback((text) => {
        if (readOnly || !XLSX || !text) return
        const rows = String(text).replace(/\r/g, '').split('\n')
        if (rows[rows.length - 1] === '') rows.pop()

        const changes = []
        rows.forEach((rowText, rowOffset) => {
            rowText.split('\t').forEach((cellText, colOffset) => {
                const r = selected.r + rowOffset
                const c = selected.c + colOffset
                const address = XLSX.utils.encode_cell({ r, c })
                changes.push({ address, nextCell: parseInputValue(cellText) })
            })
        })

        applyChanges(sheetName, changes)
        setStatus(`Вставлено ${changes.length} ячеек`)
    }, [XLSX, applyChanges, readOnly, selected.c, selected.r, sheetName])

    const addRow = useCallback(() => {
        if (readOnly || !XLSX || !sheet) return
        const nextRow = range.e.r + 1
        const address = XLSX.utils.encode_cell({ r: nextRow, c: 0 })
        applyCell(sheetName, address, { t: 's', v: '' })
        setSelected({ r: nextRow, c: 0 })
        setStatus(`Добавлена строка ${nextRow + 1}`)
    }, [XLSX, applyCell, range.e.r, readOnly, sheet, sheetName])

    const saveXlsx = useCallback(async () => {
        const currentWorkbook = workbookRef.current
        const currentXlsx = xlsxRef.current
        if (!currentWorkbook || !currentXlsx) return

        try {
            const array = currentXlsx.write(currentWorkbook, { type: 'array', bookType: 'xlsx', cellStyles: true })
            const outputName = ensureXlsxName(filename || title)
            const out = new Blob([array], { type: getWorkbookMime() })

            if (typeof onSaveFile === 'function') {
                await onSaveFile({
                    blob: out,
                    filename: outputName,
                    mimeType: getWorkbookMime(),
                    kind: 'spreadsheet',
                })
                setStatus('Изменённая копия передана на сохранение')
            } else {
                downloadBlob(out, outputName)
                setStatus('Изменённая копия XLSX сохранена')
            }
        } catch (saveError) {
            setStatus(saveError?.message || 'Не удалось сохранить XLSX')
        }
    }, [filename, onSaveFile, title])

    const saveCurrentSheetCsv = useCallback(() => {
        const currentXlsx = xlsxRef.current
        const currentSheet = workbookRef.current?.Sheets?.[sheetName]
        if (!currentXlsx || !currentSheet) return

        try {
            const csv = currentXlsx.utils.sheet_to_csv(currentSheet)
            downloadBlob(new Blob([csv], { type: 'text/csv;charset=utf-8' }), `${normalizeName(sheetName, 'sheet')}.csv`)
            setStatus('Текущий лист сохранён как CSV')
        } catch (saveError) {
            setStatus(saveError?.message || 'Не удалось сохранить CSV')
        }
    }, [sheetName])

    const filteredCells = useMemo(() => {
        const needle = search.trim().toLowerCase()
        if (!needle || !sheet || !XLSX) return null

        const matches = new Set()
        for (let r = 0; r < rowsCount; r += 1) {
            for (let c = 0; c < colsCount; c += 1) {
                const value = getCellDisplay(r, c)
                if (String(value).toLowerCase().includes(needle)) matches.add(getAddress(r, c))
            }
        }
        return matches
    }, [XLSX, colsCount, getAddress, getCellDisplay, rowsCount, search, sheet, revision])

    useEffect(() => {
        const root = rootRef.current
        if (!root) return undefined

        const onPaste = (event) => {
            if (readOnly) return
            const text = event.clipboardData?.getData?.('text/plain')
            if (!text) return
            event.preventDefault()
            pasteText(text)
        }

        const onKeyDown = (event) => {
            if (editingAddress) {
                if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault()
                    commitEdit()
                } else if (event.key === 'Escape') {
                    event.preventDefault()
                    setEditingAddress('')
                }
                return
            }

            if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'c') {
                event.preventDefault()
                copySelected()
                return
            }
            if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'z' && !event.shiftKey) {
                event.preventDefault()
                undo()
                return
            }
            if ((event.ctrlKey || event.metaKey) && (event.key.toLowerCase() === 'y' || (event.key.toLowerCase() === 'z' && event.shiftKey))) {
                event.preventDefault()
                redo()
                return
            }
            if (event.key === 'Enter') {
                event.preventDefault()
                startEdit(selected.r, selected.c)
                return
            }
            if (!readOnly && (event.key === 'Delete' || event.key === 'Backspace')) {
                event.preventDefault()
                applyCell(sheetName, selectedAddress, null)
                return
            }

            const delta = {
                ArrowUp: [-1, 0],
                ArrowDown: [1, 0],
                ArrowLeft: [0, -1],
                ArrowRight: [0, 1],
            }[event.key]

            if (delta) {
                event.preventDefault()
                setSelected(({ r, c }) => ({
                    r: Math.max(0, Math.min(rowsCount - 1, r + delta[0])),
                    c: Math.max(0, Math.min(colsCount - 1, c + delta[1])),
                }))
            }
        }

        root.addEventListener('paste', onPaste)
        root.addEventListener('keydown', onKeyDown)
        return () => {
            root.removeEventListener('paste', onPaste)
            root.removeEventListener('keydown', onKeyDown)
        }
    }, [
        applyCell,
        colsCount,
        commitEdit,
        copySelected,
        editingAddress,
        pasteText,
        readOnly,
        redo,
        rowsCount,
        selected.c,
        selected.r,
        selectedAddress,
        sheetName,
        startEdit,
        undo,
    ])

    if (loading) {
        return <div className="spreadsheet-workspace__state">Загружаем таблицу…</div>
    }

    if (error) {
        return <div className="spreadsheet-workspace__state is-error">{error}</div>
    }

    if (!workbook || !sheet || !XLSX) {
        return <div className="spreadsheet-workspace__state">Таблица недоступна</div>
    }

    return (
        <div ref={rootRef} className="spreadsheet-workspace" tabIndex={0}>
            <div className="spreadsheet-workspace__toolbar">
                <div className="spreadsheet-workspace__file">
                    <strong>{title || filename || 'Таблица'}</strong>
                    <span>{dirty ? 'Есть локальные изменения' : 'Исходный файл не изменён'}</span>
                </div>

                <label className="spreadsheet-workspace__search">
                    <Search />
                    <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Найти в видимой области" />
                </label>

                <div className="spreadsheet-workspace__actions">
                    {!readOnly ? (
                        <>
                            <button type="button" onClick={undo} disabled={historyIndex < 0} title="Отменить (Ctrl+Z)"><Undo /></button>
                            <button type="button" onClick={redo} disabled={historyIndex >= history.length - 1} title="Вернуть"><Redo /></button>
                            <button type="button" onClick={addRow} title="Добавить строку"><Add /><span>Строка</span></button>
                        </>
                    ) : null}
                    <button type="button" onClick={copySelected} title="Копировать выбранную ячейку"><ContentCopy /><span>Копировать</span></button>
                    <button type="button" onClick={saveCurrentSheetCsv} title="Скачать текущий лист CSV"><Download /><span>CSV</span></button>
                    <button type="button" className="is-primary" onClick={saveXlsx} title="Скачать изменённую копию XLSX"><SaveAlt /><span>XLSX</span></button>
                </div>
            </div>

            <div className="spreadsheet-workspace__formula-bar">
                <b>{selectedAddress}</b>
                <span>fx</span>
                <input
                    value={editingAddress === selectedAddress ? editingValue : selectedFormulaValue}
                    readOnly={readOnly}
                    onFocus={() => !readOnly && startEdit(selected.r, selected.c)}
                    onChange={(event) => setEditingValue(event.target.value)}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                            event.preventDefault()
                            commitEdit()
                        }
                        if (event.key === 'Escape') {
                            event.preventDefault()
                            setEditingAddress('')
                        }
                    }}
                    placeholder="Значение или =формула"
                />
            </div>

            <div className="spreadsheet-workspace__sheet-area">
                <table className="spreadsheet-workspace__table">
                    <thead>
                        <tr>
                            <th className="is-corner" />
                            {Array.from({ length: colsCount }, (_, c) => (
                                <th key={`h-${c}`}>{XLSX.utils.encode_col(c)}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {Array.from({ length: rowsCount }, (_, r) => (
                            <tr key={`r-${r}`}>
                                <th className="is-row-number">{r + 1}</th>
                                {Array.from({ length: colsCount }, (_unused, c) => {
                                    const address = getAddress(r, c)
                                    const isSelected = selected.r === r && selected.c === c
                                    const isEditing = editingAddress === address
                                    const matched = filteredCells?.has(address)
                                    return (
                                        <td
                                            key={address}
                                            className={`${isSelected ? 'is-selected' : ''}${matched ? ' is-match' : ''}`}
                                            onClick={() => setSelected({ r, c })}
                                            onDoubleClick={() => startEdit(r, c)}
                                        >
                                            {isEditing ? (
                                                <input
                                                    ref={editingInputRef}
                                                    value={editingValue}
                                                    onChange={(event) => setEditingValue(event.target.value)}
                                                    onBlur={commitEdit}
                                                />
                                            ) : (
                                                <span>{getCellDisplay(r, c)}</span>
                                            )}
                                        </td>
                                    )
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="spreadsheet-workspace__footer">
                <div className="spreadsheet-workspace__sheets">
                    {workbook.SheetNames.map((name) => (
                        <button
                            type="button"
                            key={name}
                            className={name === sheetName ? 'is-active' : ''}
                            onClick={() => {
                                setSheetName(name)
                                setSelected({ r: 0, c: 0 })
                                setEditingAddress('')
                            }}
                        >
                            {name}
                        </button>
                    ))}
                </div>
                <div className="spreadsheet-workspace__meta">
                    {truncatedRows || truncatedCols ? (
                        <span>Для производительности показано: {rowsCount} строк × {colsCount} колонок</span>
                    ) : (
                        <span>{rowsCount} строк × {colsCount} колонок</span>
                    )}
                    {status ? <b>{status}</b> : null}
                </div>
            </div>
        </div>
    )
}
